'use client';
import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import AppLayout from '@/components/AppLayout';
import { Archive, getAllPublishedArchives } from '@/lib/db/archives';
import { createClient } from '@/lib/supabase/client';
import { BookMarked, MapPin, Users, Search, Loader2, ArrowRight, PlusCircle, Filter, X, ChevronDown, SlidersHorizontal, LayoutGrid, List, Clock } from 'lucide-react';

interface Era {
  id: string;
  label: string;
  dateRange: string | null;
  sortOrder: number;
}

interface Topic {
  id: string;
  eraId: string;
  label: string;
}

const AFRICAN_REGIONS = [
  'East Africa', 'West Africa', 'North Africa', 'Southern Africa',
  'Central Africa', 'Horn of Africa', 'Great Lakes', 'Sahel',
  'Zimbabwe', 'South Africa', 'Nigeria', 'Kenya', 'Ethiopia',
  'Egypt', 'Morocco', 'Ghana', 'Senegal', 'Tanzania', 'Uganda',
];

type SortOption = 'newest' | 'oldest' | 'most-contributions' | 'alphabetical';

function ArchiveCard({ archive, viewMode }: { archive: Archive; viewMode: 'grid' | 'list' }) {
  const publishedDate = archive.publishedAt
    ? new Date(archive.publishedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  if (viewMode === 'list') {
    return (
      <Link
        href={`/archives/${archive.id}`}
        className="group flex items-center gap-5 rounded-xl border p-4 transition-all duration-200"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--primary)';
          (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 2px 16px rgba(193,122,62,0.10)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border)';
          (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
        }}
      >
        <div
          className="w-1 self-stretch rounded-full flex-shrink-0"
          style={{ backgroundColor: 'var(--primary)', opacity: 0.7 }}
        />
        <div className="flex-1 min-w-0">
          <h3
            className="text-sm font-semibold leading-snug mb-1 group-hover:text-amber-500 transition-colors duration-150 truncate"
            style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}
          >
            {archive.title}
          </h3>
          <p className="text-xs line-clamp-1" style={{ color: 'var(--muted-foreground)' }}>
            {archive.description}
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-4 flex-shrink-0 text-xs" style={{ color: 'var(--muted-foreground)' }}>
          {archive.region && (
            <span className="flex items-center gap-1">
              <MapPin size={11} />
              {archive.region}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Users size={11} />
            {archive.contributionCount}
          </span>
          {publishedDate && <span>{publishedDate}</span>}
        </div>
        <ArrowRight
          size={14}
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex-shrink-0"
          style={{ color: 'var(--primary)' }}
        />
      </Link>
    );
  }

  return (
    <Link
      href={`/archives/${archive.id}`}
      className="group flex flex-col rounded-xl border overflow-hidden transition-all duration-200 hover:-translate-y-0.5"
      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--primary)';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 4px 24px rgba(193,122,62,0.12)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border)';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
      }}
    >
      <div className="h-1 w-full" style={{ backgroundColor: 'var(--primary)', opacity: 0.7 }} />
      <div className="flex flex-col flex-1 p-5 gap-3">
        <h3
          className="text-base font-semibold leading-snug line-clamp-2 group-hover:text-amber-500 transition-colors duration-150"
          style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}
        >
          {archive.title}
        </h3>
        <p className="text-sm line-clamp-3 flex-1" style={{ color: 'var(--muted-foreground)' }}>
          {archive.description}
        </p>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          {archive.region && (
            <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
              <MapPin size={11} />
              {archive.region}
            </span>
          )}
          <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Users size={11} />
            {archive.contributionCount} contribution{archive.contributionCount !== 1 ? 's' : ''}
          </span>
          {publishedDate && (
            <span className="text-xs ml-auto" style={{ color: 'var(--muted-foreground)' }}>
              {publishedDate}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between pt-1 border-t" style={{ borderColor: 'var(--border)' }}>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            by <span style={{ color: 'var(--foreground)' }}>{archive.creatorName}</span>
          </span>
          <ArrowRight
            size={14}
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-150"
            style={{ color: 'var(--primary)' }}
          />
        </div>
      </div>
    </Link>
  );
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium"
      style={{ backgroundColor: '#C17A3E22', color: 'var(--primary)', border: '1px solid #C17A3E44' }}
    >
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="hover:opacity-70 transition-opacity"
        aria-label={`Remove ${label} filter`}
      >
        <X size={11} />
      </button>
    </span>
  );
}

export default function ArchivesBrowsePage() {
  const [archives, setArchives] = useState<Archive[]>([]);
  const [eras, setEras] = useState<Era[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [selectedEra, setSelectedEra] = useState<string>('');
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const supabase = createClient();
    Promise.all([
      getAllPublishedArchives(),
      supabase.from('eras').select('id, label, date_range, sort_order').order('sort_order'),
      supabase.from('topics').select('id, era_id, label').order('label'),
    ])
      .then(([archivesData, erasRes, topicsRes]) => {
        setArchives(archivesData);
        setEras(
          (erasRes.data || []).map((e: any) => ({
            id: e.id,
            label: e.label,
            dateRange: e.date_range,
            sortOrder: e.sort_order,
          }))
        );
        setTopics(
          (topicsRes.data || []).map((t: any) => ({
            id: t.id,
            eraId: t.era_id,
            label: t.label,
          }))
        );
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Derive unique regions from archives
  const availableRegions = useMemo(() => {
    const regions = new Set<string>();
    archives.forEach((a) => { if (a.region) regions.add(a.region); });
    return Array.from(regions).sort();
  }, [archives]);

  // Topics filtered by selected era
  const filteredTopics = useMemo(() => {
    if (!selectedEra) return topics;
    return topics.filter((t) => t.eraId === selectedEra);
  }, [topics, selectedEra]);

  // Apply all filters + sort
  const filtered = useMemo(() => {
    let result = [...archives];

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.description.toLowerCase().includes(q) ||
          (a.region || '').toLowerCase().includes(q) ||
          (a.creatorName || '').toLowerCase().includes(q)
      );
    }

    if (selectedEra) {
      result = result.filter((a) => a.eraId === selectedEra);
    }

    if (selectedTopic) {
      result = result.filter((a) => a.topicId === selectedTopic);
    }

    if (selectedRegion) {
      result = result.filter((a) => a.region === selectedRegion);
    }

    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || ''));
        break;
      case 'oldest':
        result.sort((a, b) => (a.publishedAt || '').localeCompare(b.publishedAt || ''));
        break;
      case 'most-contributions':
        result.sort((a, b) => b.contributionCount - a.contributionCount);
        break;
      case 'alphabetical':
        result.sort((a, b) => a.title.localeCompare(b.title));
        break;
    }

    return result;
  }, [archives, search, selectedEra, selectedTopic, selectedRegion, sortBy]);

  const activeFilterCount = [selectedEra, selectedTopic, selectedRegion].filter(Boolean).length;

  const clearAllFilters = () => {
    setSelectedEra('');
    setSelectedTopic('');
    setSelectedRegion('');
    setSearch('');
  };

  const selectedEraLabel = eras.find((e) => e.id === selectedEra)?.label;
  const selectedTopicLabel = topics.find((t) => t.id === selectedTopic)?.label;

  return (
    <AppLayout>
      <div className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--background)' }}>
        <div className="max-w-screen-xl mx-auto">

          {/* Page header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <BookMarked size={18} style={{ color: 'var(--primary)' }} />
              <span
                className="text-xs font-mono uppercase tracking-widest"
                style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}
              >
                Browse Archives
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h1
                  className="text-3xl sm:text-4xl font-bold mb-2"
                  style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}
                >
                  Explore All Archives
                </h1>
                <p className="text-base max-w-2xl" style={{ color: 'var(--muted-foreground)' }}>
                  Filter and discover curated collections of historical records by era, theme, and region across Africa.
                </p>
              </div>
              <Link
                href="/contribution-submission"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium flex-shrink-0 self-start sm:self-auto"
                style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
              >
                <PlusCircle size={15} />
                Submit a Story
              </Link>
            </div>
          </div>

          {/* Search + toolbar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
              <input
                type="text"
                placeholder="Search archives by title, description, region, or curator…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-lg border text-sm outline-none transition-colors duration-150"
                style={{
                  backgroundColor: 'var(--card)',
                  borderColor: 'var(--border)',
                  color: 'var(--foreground)',
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--primary)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
              />
            </div>

            {/* Filter toggle */}
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all duration-150 flex-shrink-0"
              style={{
                backgroundColor: showFilters ? '#C17A3E15' : 'var(--card)',
                borderColor: showFilters ? 'var(--primary)' : 'var(--border)',
                color: showFilters ? 'var(--primary)' : 'var(--foreground)',
              }}
            >
              <SlidersHorizontal size={15} />
              Filters
              {activeFilterCount > 0 && (
                <span
                  className="w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center"
                  style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
                >
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Sort */}
            <div className="relative flex-shrink-0">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="appearance-none pl-3 pr-8 py-2.5 rounded-lg border text-sm outline-none cursor-pointer"
                style={{
                  backgroundColor: 'var(--card)',
                  borderColor: 'var(--border)',
                  color: 'var(--foreground)',
                }}
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="most-contributions">Most contributions</option>
                <option value="alphabetical">A–Z</option>
              </select>
              <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
            </div>

            {/* View mode */}
            <div
              className="flex items-center rounded-lg border overflow-hidden flex-shrink-0"
              style={{ borderColor: 'var(--border)' }}
            >
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className="p-2.5 transition-colors duration-150"
                style={{
                  backgroundColor: viewMode === 'grid' ? 'var(--primary)' : 'var(--card)',
                  color: viewMode === 'grid' ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
                }}
                aria-label="Grid view"
              >
                <LayoutGrid size={15} />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className="p-2.5 transition-colors duration-150"
                style={{
                  backgroundColor: viewMode === 'list' ? 'var(--primary)' : 'var(--card)',
                  color: viewMode === 'list' ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
                }}
                aria-label="List view"
              >
                <List size={15} />
              </button>
            </div>
          </div>

          {/* Filter panel */}
          {showFilters && (
            <div
              className="rounded-xl border p-5 mb-5 grid grid-cols-1 sm:grid-cols-3 gap-4"
              style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
            >
              {/* Era filter */}
              <div>
                <label className="block text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
                  Era
                </label>
                <div className="relative">
                  <select
                    value={selectedEra}
                    onChange={(e) => {
                      setSelectedEra(e.target.value);
                      setSelectedTopic('');
                    }}
                    className="w-full appearance-none pl-3 pr-8 py-2 rounded-lg border text-sm outline-none cursor-pointer"
                    style={{
                      backgroundColor: 'var(--background)',
                      borderColor: selectedEra ? 'var(--primary)' : 'var(--border)',
                      color: 'var(--foreground)',
                    }}
                  >
                    <option value="">All eras</option>
                    {eras.map((era) => (
                      <option key={era.id} value={era.id}>
                        {era.label}{era.dateRange ? ` (${era.dateRange})` : ''}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
                </div>
              </div>

              {/* Theme / Topic filter */}
              <div>
                <label className="block text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
                  Theme / Topic
                </label>
                <div className="relative">
                  <select
                    value={selectedTopic}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                    className="w-full appearance-none pl-3 pr-8 py-2 rounded-lg border text-sm outline-none cursor-pointer"
                    style={{
                      backgroundColor: 'var(--background)',
                      borderColor: selectedTopic ? 'var(--primary)' : 'var(--border)',
                      color: 'var(--foreground)',
                    }}
                  >
                    <option value="">All themes</option>
                    {filteredTopics.map((topic) => (
                      <option key={topic.id} value={topic.id}>
                        {topic.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
                </div>
              </div>

              {/* Region filter */}
              <div>
                <label className="block text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
                  Region
                </label>
                <div className="relative">
                  <select
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                    className="w-full appearance-none pl-3 pr-8 py-2 rounded-lg border text-sm outline-none cursor-pointer"
                    style={{
                      backgroundColor: 'var(--background)',
                      borderColor: selectedRegion ? 'var(--primary)' : 'var(--border)',
                      color: 'var(--foreground)',
                    }}
                  >
                    <option value="">All regions</option>
                    {availableRegions.length > 0
                      ? availableRegions.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))
                      : AFRICAN_REGIONS.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))
                    }
                  </select>
                  <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted-foreground)' }} />
                </div>
              </div>
            </div>
          )}

          {/* Active filter chips */}
          {(activeFilterCount > 0 || search) && (
            <div className="flex flex-wrap items-center gap-2 mb-5">
              <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Active filters:</span>
              {selectedEraLabel && (
                <FilterChip label={`Era: ${selectedEraLabel}`} onRemove={() => { setSelectedEra(''); setSelectedTopic(''); }} />
              )}
              {selectedTopicLabel && (
                <FilterChip label={`Theme: ${selectedTopicLabel}`} onRemove={() => setSelectedTopic('')} />
              )}
              {selectedRegion && (
                <FilterChip label={`Region: ${selectedRegion}`} onRemove={() => setSelectedRegion('')} />
              )}
              {search && (
                <FilterChip label={`"${search}"`} onRemove={() => setSearch('')} />
              )}
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-xs underline transition-opacity hover:opacity-70"
                style={{ color: 'var(--muted-foreground)' }}
              >
                Clear all
              </button>
            </div>
          )}

          {/* Stats row */}
          {!loading && (
            <div className="flex items-center gap-4 mb-6">
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                <span className="font-semibold" style={{ color: 'var(--foreground)' }}>{filtered.length}</span>
                {' '}archive{filtered.length !== 1 ? 's' : ''} found
                {archives.length !== filtered.length && (
                  <span> of {archives.length} total</span>
                )}
              </p>
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 size={32} className="animate-spin" style={{ color: 'var(--muted-foreground)' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 text-center">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6"
                style={{ backgroundColor: '#C17A3E11', border: '1px dashed #C17A3E44' }}
              >
                <BookMarked size={36} style={{ color: 'var(--primary)', opacity: 0.5 }} />
              </div>
              <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--foreground)' }}>
                No archives match your filters
              </h2>
              <p className="text-sm max-w-sm mb-6" style={{ color: 'var(--muted-foreground)' }}>
                Try adjusting your search or removing some filters to see more results.
              </p>
              <button
                onClick={clearAllFilters}
                className="px-4 py-2 rounded-lg text-sm font-medium border"
                style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
              >
                Clear all filters
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((archive) => (
                <ArchiveCard key={archive.id} archive={archive} viewMode="grid" />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {filtered.map((archive) => (
                <ArchiveCard key={archive.id} archive={archive} viewMode="list" />
              ))}
            </div>
          )}

          {/* Era quick-browse section (only when no filters active) */}
          {!loading && activeFilterCount === 0 && !search && eras.length > 0 && (
            <div className="mt-16 pt-10 border-t" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-2 mb-6">
                <Clock size={16} style={{ color: 'var(--primary)' }} />
                <h2 className="text-lg font-semibold" style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}>
                  Browse by Era
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {eras.map((era) => {
                  const count = archives.filter((a) => a.eraId === era.id).length;
                  return (
                    <button
                      key={era.id}
                      type="button"
                      onClick={() => {
                        setSelectedEra(era.id);
                        setShowFilters(true);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="text-left rounded-xl border p-4 transition-all duration-150 group"
                      style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--primary)';
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#C17A3E08';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)';
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'var(--card)';
                      }}
                    >
                      <p
                        className="text-sm font-semibold mb-1 group-hover:text-amber-500 transition-colors duration-150"
                        style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}
                      >
                        {era.label}
                      </p>
                      {era.dateRange && (
                        <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)', fontFamily: 'var(--font-mono)' }}>
                          {era.dateRange}
                        </p>
                      )}
                      <p className="text-xs" style={{ color: 'var(--primary)' }}>
                        {count} archive{count !== 1 ? 's' : ''}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
